(() => {
  if (window.__awWhyKlugLoaded) {
    return;
  }

  window.__awWhyKlugLoaded = true;

  const instances = new Map();
  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const selectors = {
    root: "[data-aw-why-klug]",
    scroll: "[data-aw-why-scroll]",
    sticky: "[data-aw-why-sticky]",
    stage: "[data-aw-why-stage]",
    slide: "[data-aw-why-slide]",
    image: ".aw-why-klug__image",
  };

  class AWWhyKlug {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || "";
      this.enableParallax = root.dataset.enableParallax === "true";
      this.scrollEl = root.querySelector(selectors.scroll);
      this.stickyEl = root.querySelector(selectors.sticky);
      this.stageEl = root.querySelector(selectors.stage);
      this.slides = Array.from(root.querySelectorAll(selectors.slide));
      this.images = Array.from(root.querySelectorAll(selectors.image));
      this.activeIndex = 0;
      this.rafId = null;
      this.resizeObserver = null;
      this.isStatic =
        reducedMotionQuery.matches ||
        !this.enableParallax ||
        this.slides.length <= 1 ||
        !this.scrollEl ||
        !this.stickyEl ||
        !this.stageEl;

      this.onScroll = this.requestUpdate.bind(this);
      this.onResize = this.requestUpdate.bind(this);
      this.onImageLoad = this.requestUpdate.bind(this);
      this.onObservedResize = this.requestUpdate.bind(this);

      this.init();
    }

    init() {
      if (!this.slides.length) {
        return;
      }

      this.root.classList.add("is-enhanced");
      this.bindEvents();
      this.updateStageSize();
      this.activate(0);
      this.resetParallax();

      if (this.isStatic) {
        this.root.classList.add("is-static");
        return;
      }

      this.update();
    }

    bindEvents() {
      window.addEventListener("resize", this.onResize);

      if (!this.isStatic) {
        window.addEventListener("scroll", this.onScroll, { passive: true });
      }

      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(this.onObservedResize);
        this.resizeObserver.observe(this.root);
        this.resizeObserver.observe(this.stageEl);
      }

      this.images.forEach((image) => {
        if (!image.complete) {
          image.addEventListener("load", this.onImageLoad);
        }
      });
    }

    updateStageSize() {
      if (!this.stageEl || !this.stickyEl) {
        return;
      }

      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      const ratio = isMobile ? 608 / 342 : 990 / 1760;
      const sidePadding = isMobile ? 40 : 80;
      const maxDesignWidth = isMobile ? Number.POSITIVE_INFINITY : 1760;
      const verticalSafeGap = 16;
      const availableWidth = Math.max(1, window.innerWidth - sidePadding);
      const availableHeight = Math.max(1, window.innerHeight - verticalSafeGap * 2);
      const widthFromViewport = Math.min(availableWidth, maxDesignWidth);
      const widthFromHeight = availableHeight / ratio;
      const width = Math.max(1, Math.min(widthFromViewport, widthFromHeight));
      const height = width * ratio;
      const slideCount = Math.max(1, this.slides.length);
      const scrollStep = height * 0.72;
      const scrollHeight = height + scrollStep * Math.max(0, slideCount - 1);
      const stickyTop = Math.max(verticalSafeGap, (window.innerHeight - height) / 2);

      this.root.style.setProperty("--aw-why-stage-width-current", `${width}px`);
      this.root.style.setProperty("--aw-why-stage-height-current", `${height}px`);
      this.root.style.setProperty("--aw-why-scroll-height-current", `${scrollHeight}px`);
      this.root.style.setProperty("--aw-why-sticky-top-current", `${stickyTop}px`);
    }

    getStickyTop() {
      if (!this.stickyEl) {
        return 0;
      }

      return Number.parseFloat(window.getComputedStyle(this.stickyEl).top || "0") || 0;
    }

    getScrollLength() {
      if (!this.scrollEl || !this.stickyEl) {
        return 1;
      }

      return Math.max(1, this.scrollEl.offsetHeight - this.stickyEl.offsetHeight);
    }

    getProgress() {
      if (!this.scrollEl || !this.stickyEl) {
        return 0;
      }

      const rect = this.scrollEl.getBoundingClientRect();
      const scrollLength = this.getScrollLength();
      const stickyTop = this.getStickyTop();
      const progress = (stickyTop - rect.top) / scrollLength;

      return Math.max(0, Math.min(1, progress));
    }

    getIndexFromProgress(progress) {
      if (!this.slides.length) {
        return 0;
      }

      if (this.slides.length === 1) {
        return 0;
      }

      const clampedProgress = Math.max(0, Math.min(1, progress));
      const rawIndex = Math.round(clampedProgress * (this.slides.length - 1));

      return Math.min(this.slides.length - 1, Math.max(0, rawIndex));
    }

    requestUpdate() {
      if (this.rafId) {
        return;
      }

      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = null;
        this.updateStageSize();

        if (this.isStatic) {
          this.resetParallax();
          return;
        }

        this.update();
      });
    }

    update() {
      if (this.isStatic) {
        return;
      }

      const progress = this.getProgress();
      const index = this.getIndexFromProgress(progress);

      this.activate(index);
      this.resetParallax();
    }

    resetParallax() {
      this.slides.forEach((slide) => {
        slide.style.setProperty("--aw-why-parallax-y", "0px");
      });
    }

    updateFocusableState(slide, isActive) {
      slide.querySelectorAll("a").forEach((link) => {
        if (isActive) {
          link.removeAttribute("tabindex");
        } else {
          link.setAttribute("tabindex", "-1");
        }
      });
    }

    activate(index) {
      if (index < 0 || index >= this.slides.length) {
        return;
      }

      this.activeIndex = index;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;

        slide.classList.toggle("is-active", isActive);
        slide.setAttribute("aria-hidden", isActive ? "false" : "true");
        this.updateFocusableState(slide, isActive);
      });
    }

    selectBlock(blockId, fallbackTarget) {
      let slide = blockId ? this.root.querySelector(`${selectors.slide}[data-block-id="${blockId}"]`) : null;

      if (!slide && fallbackTarget && typeof fallbackTarget.closest === "function") {
        slide = fallbackTarget.closest(selectors.slide);
      }

      if (!slide) {
        return;
      }

      const index = this.slides.indexOf(slide);

      if (index < 0) {
        return;
      }

      this.activate(index);
      this.resetParallax();
      this.updateStageSize();

      if (this.isStatic || !this.scrollEl) {
        this.root.scrollIntoView({
          behavior: reducedMotionQuery.matches ? "auto" : "smooth",
          block: "start",
        });
        return;
      }

      const sectionTop = window.scrollY + this.scrollEl.getBoundingClientRect().top;
      const stickyTop = this.getStickyTop();
      const scrollLength = this.getScrollLength();
      const targetProgress = this.slides.length > 1 ? index / (this.slides.length - 1) : 0;
      const targetY = sectionTop - stickyTop + scrollLength * targetProgress;

      window.scrollTo({
        top: targetY,
        behavior: reducedMotionQuery.matches ? "auto" : "smooth",
      });

      this.requestUpdate();
    }

    destroy() {
      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onResize);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      this.images.forEach((image) => {
        image.removeEventListener("load", this.onImageLoad);
      });
    }
  }

  function getRoot(target) {
    if (!target) {
      return null;
    }

    if (typeof target.matches === "function" && target.matches(selectors.root)) {
      return target;
    }

    if (typeof target.querySelector === "function") {
      const root = target.querySelector(selectors.root);

      if (root) {
        return root;
      }
    }

    if (typeof target.closest === "function") {
      return target.closest(selectors.root);
    }

    return null;
  }

  function destroySection(sectionId) {
    const instance = instances.get(sectionId);

    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(sectionId);
  }

  function initSection(root) {
    if (!root) {
      return;
    }

    const sectionId = root.dataset.sectionId;

    if (!sectionId) {
      return;
    }

    destroySection(sectionId);
    instances.set(sectionId, new AWWhyKlug(root));
  }

  function initAll(container = document) {
    container.querySelectorAll(selectors.root).forEach((root) => {
      initSection(root);
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    initSection(getRoot(event.target));
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = getRoot(event.target);

    if (!root) {
      return;
    }

    destroySection(root.dataset.sectionId);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    const blockId = event.detail && event.detail.blockId;
    const instance = sectionId ? instances.get(sectionId) : null;

    if (!instance) {
      return;
    }

    instance.selectBlock(blockId, event.target);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initAll(document);
    });
  } else {
    initAll(document);
  }
})();
